/* eslint-disable import/no-duplicates */
import { useTranslation } from 'react-i18next';
import { useMemo, PropsWithChildren, useEffect, useCallback } from 'react';
import { Locale } from 'date-fns';
import { enUS, frCA, es } from 'date-fns/locale';
import { useFeatureFlags } from '@water-web/view';

import { AppLocale, geti18n, getMaybePersistedLocale, saveUserLocale } from '@app/i18n';
import { detectBrowserLocale } from '@app/utils/detectBrowserLocale';

import { LocaleContext, LocaleContextValue } from './LocaleContext';
import { getDateFormats } from './getDateFormats';
import { getTimeFormat } from './getTimeFormat';
import { getFormatRelative } from './getFormatRelative';

const getDateFnsLocaleByLanguage = (language: AppLocale | string): Locale => {
  switch (language) {
    case AppLocale.fr:
      return frCA;
    case AppLocale.es:
      return es;
    case AppLocale.en:
    default:
      return enUS;
  }
};

const getInitialLocale = (clientBookingLocalization: boolean) => {
  const persistedLocale = getMaybePersistedLocale();

  if (persistedLocale) {
    return persistedLocale;
  }

  if (clientBookingLocalization) {
    return detectBrowserLocale();
  }
  return AppLocale.en;
};

const getSupportedLanguages = (clientBookingLocalization: boolean) => {
  if (clientBookingLocalization) {
    return {
      [AppLocale.en]: true,
      [AppLocale.fr]: true,
      [AppLocale.es]: true,
    };
  }
  return {
    [AppLocale.en]: true,
    [AppLocale.fr]: true,
    [AppLocale.es]: false,
  };
};

export const LocaleProvider = ({ children }: PropsWithChildren) => {
  const i18n = geti18n();
  const { t } = useTranslation();
  const { clientBookingLocalization } = useFeatureFlags();

  const locale = i18n.language as AppLocale;
  const dateFnsLocale = getDateFnsLocaleByLanguage(locale);
  const supportedLanguages = getSupportedLanguages(clientBookingLocalization);

  const changeLocale = useCallback(
    (nextLocale: AppLocale) => {
      i18n.changeLanguage(nextLocale);
      saveUserLocale(nextLocale);
    },
    [i18n],
  );

  useEffect(() => {
    const newInitialLocale = getInitialLocale(clientBookingLocalization);
    changeLocale(newInitialLocale);
  }, [clientBookingLocalization, changeLocale]);

  const value = useMemo<LocaleContextValue>(() => {
    return {
      t,
      locale,
      setLocale: changeLocale,
      supportedLanguages,
      dateFnsLocale: {
        ...dateFnsLocale,
        ...getFormatRelative(t),
      },
      dateFormats: getDateFormats(t),
      timeFormat: getTimeFormat(t),
    };
  }, [locale, changeLocale, supportedLanguages, t, dateFnsLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};
